import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Save, ClipboardList } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = [["text", "Short answer"], ["textarea", "Long answer"], ["select", "Multiple choice"], ["yes_no", "Yes / No"]];

export const IntakeFormEditor = ({ endpoint, subtitle }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["intake-form", endpoint], queryFn: () => api.get(endpoint).then((r) => r.data) });
  const [questions, setQuestions] = useState([]);
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) { setQuestions(data.questions || []); setActive(data.questions?.length ? data.active : true); }
  }, [data]);

  const upd = (i, patch) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const add = () => setQuestions((qs) => [...qs, { id: "", label: "", type: "text", options: [], required: false }]);
  const remove = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(endpoint, { questions, active });
      toast.success("Intake form saved");
      qc.invalidateQueries({ queryKey: ["intake-form", endpoint] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (isLoading) return <p className="text-sm text-[#4A5D6B] flex items-center gap-2 py-6"><Loader2 className="animate-spin" size={16} /> Loading…</p>;

  return (
    <div data-testid="intake-form-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ClipboardList size={20} className="text-[#144F75]" /> Intake form</h2>
          <p className="text-sm text-[#4A5D6B]">{subtitle || "New clients complete these questions before their first session with you. Responses save to their profile."}</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A5D6B] cursor-pointer">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#144F75]" data-testid="intake-form-active" /> Form active
        </label>
      </div>
      <div className="space-y-3">
        {questions.map((q, i) => (
          <div key={i} className="eyw-card p-4" data-testid={`intake-q-${i}`}>
            <div className="flex flex-wrap gap-3 items-start">
              <input value={q.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder={`Question ${i + 1} — e.g. "Any injuries or areas of concern?"`} data-testid={`intake-q-label-${i}`} className="eyw-input flex-1 min-w-[220px]" />
              <select value={q.type} onChange={(e) => upd(i, { type: e.target.value })} data-testid={`intake-q-type-${i}`} className="eyw-input">
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-[#4A5D6B] pt-3 cursor-pointer">
                <input type="checkbox" checked={q.required} onChange={(e) => upd(i, { required: e.target.checked })} className="accent-[#144F75]" /> Required
              </label>
              <button onClick={() => remove(i)} data-testid={`intake-q-remove-${i}`} className="text-red-400 hover:text-red-600 pt-2.5"><Trash2 size={16} /></button>
            </div>
            {q.type === "select" && (
              <input value={(q.options || []).join(", ")} onChange={(e) => upd(i, { options: e.target.value.split(",").map((s) => s.trimStart()) })}
                placeholder="Choices, separated by commas — e.g. Morning, Afternoon, Evening" data-testid={`intake-q-options-${i}`} className="eyw-input w-full mt-2 text-sm" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={add} data-testid="intake-add-question" className="eyw-btn-ghost !py-2 text-sm"><Plus size={15} /> Add question</button>
        <button onClick={save} disabled={saving} data-testid="intake-form-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save form</button>
      </div>
      <p className="text-xs text-[#8FA89B] mt-3">Clients see your questions merged with the center's questions in one short form. Returning clients are only asked again when you request an update from their profile.</p>
    </div>
  );
};

export default IntakeFormEditor;
